import React from 'react';
import styles from './BasicInfoCard.module.css';
import { applySkillOrder } from '../../../../shared/utils/skillOrder';

const GENDER_KO = { MALE: '남성', FEMALE: '여성'};

function calcAge(birthDate) {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function InfoRow({ items }) {
  return (
    <div className={styles.infoRow}>
      {items.map((item, i) => (
        <div key={i} className={styles.infoItem}>
          <span className={styles.label}>{item.label}</span>
          <span className={styles.value}>{item.value || '—'}</span>
        </div>
      ))}
    </div>
  );
}

function InfoBlock({ label, children }) {
  return (
    <div className={styles.block}>
      <span className={styles.label}>{label}</span>
      {children}
    </div>
  );
}

function ContactItem({ icon, text }) {
  if (!text) return null;
  return (
    <div className={styles.contactItem}>
      <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{icon}</span>
      <span className={styles.contactText}>{text}</span>
    </div>
  );
}

function BasicInfoCard({ profile, skills: skillsProp, onFetchSkills, skillsLoading }) {
  const gender = GENDER_KO[profile?.gender] ?? '';
  const age = calcAge(profile?.birthDate);
  const birthYear = profile?.birthDate ? new Date(profile.birthDate).getFullYear() : null;
  const rawSkills = skillsProp ?? profile?.skills?.map((s) => s.name || s.skillName || s) ?? [];
  const skills = applySkillOrder(profile?.id, rawSkills);
  const contactEmail = profile?.contactEmail || profile?.email || profile?.user?.email || '';
  const rawPhone = profile?.phone || profile?.contactPhone || profile?.user?.phone || '';
  const digits = rawPhone.replace(/\D/g, '');
  const phone = digits.length === 11
    ? `${digits.slice(0,3)}-${digits.slice(3,7)}-${digits.slice(7)}`
    : digits.length === 10
    ? `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`
    : rawPhone;
  const instagramRaw = profile?.instagramUrl || profile?.instagram || '';
  // 아이디만 저장된 경우 @ 붙여서 표시
  const instagramUrl = instagramRaw
    ? (instagramRaw.startsWith('http') ? instagramRaw : `@${instagramRaw.replace(/^@+/, '')}`)
    : '';
  const youtubeUrl = profile?.youtubeUrl || profile?.youtube || '';

  return (
    <div className={styles.container}>
      {/* 기본정보 */}
      <div className={styles.block} style={{ gap: '18px' }}>
        <span className={styles.sectionTitle}>기본정보</span>
        <div className={styles.card}>
          <div className={styles.rowGroup}>
            <InfoRow items={[
              { label: '성별', value: gender },
              { label: '나이', value: age !== null ? `${age}세${birthYear ? ` (${birthYear}년생)` : ''}` : '' },
            ]} />
            <InfoRow items={[
              { label: '키', value: profile?.heightCm ? `${profile.heightCm}cm` : '' },
              { label: '몸무게', value: profile?.weightKg ? `${profile.weightKg}kg` : '' },
            ]} />
          </div>
          <InfoBlock label="위치">
            <span className={styles.value}>{profile?.residence || '—'}</span>
          </InfoBlock>
          <InfoBlock label="특기">
            {skillsLoading ? null : skills.length > 0 ? (
              <div className={styles.tagContainer}>
                {skills.map((s) => (
                  <div key={s} className={styles.tag}>
                    <span className={styles.tagText}>{s}</span>
                  </div>
                ))}
              </div>
            ) : (
              <span className={styles.emptyText}>등록된 특기가 없습니다.</span>
            )}
          </InfoBlock>
        </div>
      </div>

      {/* Contact — 항상 표시 */}
      <div className={styles.contactCard}>
        <span className={styles.contactLabel}>Contact</span>
        <ContactItem text={contactEmail} icon={
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="2" y="4" width="20" height="16" rx="2" stroke="#A9A9B2" strokeWidth="1.5"/><path d="M2 7l10 7 10-7" stroke="#A9A9B2" strokeWidth="1.5" strokeLinecap="round"/></svg>
        }/>
        <ContactItem text={phone} icon={
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M22 16.92v3a2 2 0 01-2.18 2A19.79 19.79 0 012.09 5.18 2 2 0 014.07 3h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 10.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="#A9A9B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        }/>
        <ContactItem text={instagramUrl} icon={
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="2" y="2" width="20" height="20" rx="5" stroke="#A9A9B2" strokeWidth="1.5"/><circle cx="12" cy="12" r="4" stroke="#A9A9B2" strokeWidth="1.5"/><circle cx="17.5" cy="6.5" r="1" fill="#A9A9B2"/></svg>
        }/>
        <ContactItem text={youtubeUrl} icon={
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 00-1.95 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58z" stroke="#A9A9B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" stroke="#A9A9B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        }/>
        {!contactEmail && !phone && !instagramUrl && !youtubeUrl && (
          <span style={{ fontSize: '13px', color: '#A9A9B2', fontFamily: 'Pretendard, sans-serif' }}>등록된 연락처가 없습니다.</span>
        )}
      </div>
    </div>
  );
}

export default BasicInfoCard;
